// SETTINGS V2 — Doprava edit dialog (SETTINGS_V2_SP9 §6/§7). SettingsV2DialogShell
// consumer, 880px desktop / fullscreen phone. Open-state = settingsV2Ui.openDialog
// === "shipping"; seeds controlled state from customerShippingOptions.shippingOptions
// on open (§6 — replaces legacy's remount-key trick). Binds the reused
// customerShippingOptions slice UNCHANGED. The pure form model / seed / validate /
// graph-rebuild live in shippingV2Utils (kept the dialog < 300 lines). Close FIRST
// then PUT then refetch (parity order). FIX #1: fulfilled PUT → setSuccess feeds the
// page's real success toast. Inline styles + scoped CSS-var tokens + v2 atoms; no
// MUI/sx/dt/Tailwind/moment.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import {
  changeShippingOptions,
  getShippingOptions,
  setSuccess,
} from "../../customerShippingOptionsSlice";
import { closeDialog } from "../settingsV2UiSlice";
import { SETTINGS_BASE } from "../settingsV2Paths";
import { SCheck } from "../atoms";
import SettingsV2DialogShell from "./SettingsV2DialogShell";
import ShippingRegionRowV2, {
  ShippingRegionRowFieldErrors,
} from "./ShippingRegionRowV2";
import {
  EMPTY_FORM,
  REGIONS,
  REGION_CODE,
  Row,
  ShipRegion,
  ShipType,
  ShippingForm,
  buildShippingGraph,
  compressWeekDaysLocalized,
  formatHHmm,
  regionLabel,
  seedForm,
  validateShippingForm,
} from "../pages/shippingV2Utils";

type TabId = "standart" | "express" | "pickUp";

const ShippingDialogV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();

  const open = useAppSelector((s) => s.settingsV2Ui.openDialog === "shipping");
  const shippingOptions = useAppSelector(
    (s) => s.customerShippingOptions.shippingOptions
  );
  const warehouse = shippingOptions?.warehouse; // RUNTIME-NULLABLE (§2 guard)
  const countryId = shippingOptions?.countryId ?? 0;

  const [form, setForm] = useState<ShippingForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<TabId>("standart");

  // Seed on open (§6) — controlled-state translation of legacy defaultValues.
  useEffect(() => {
    if (!open) return;
    setForm(seedForm(shippingOptions?.options ?? []));
    setErrors({});
    setTab("standart");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => dispatch(closeDialog());

  const goToCompany = () => {
    dispatch(closeDialog());
    navigate(`${SETTINGS_BASE}/company`); // FIX #3 — real company page (not index→profile)
  };

  // Explicit-literal update (no computed union keys → strict-tsc safe).
  const setRow = (type: ShipType, region: ShipRegion, patch: Partial<Row>) =>
    setForm((f) => {
      const applyRegion = (rows: Record<ShipRegion, Row>): Record<ShipRegion, Row> => ({
        country: region === "country" ? { ...rows.country, ...patch } : rows.country,
        europeanUnion:
          region === "europeanUnion" ? { ...rows.europeanUnion, ...patch } : rows.europeanUnion,
        worldwide: region === "worldwide" ? { ...rows.worldwide, ...patch } : rows.worldwide,
      });
      return {
        standart: type === "standart" ? applyRegion(f.standart) : f.standart,
        express: type === "express" ? applyRegion(f.express) : f.express,
        isSelfPickUpActive: f.isSelfPickUpActive,
      };
    });

  // clearErrors parity (§2): any row's isActive OR from change clears all errors.
  const handleRowToggle = (type: ShipType, region: ShipRegion, next: boolean) => {
    setRow(type, region, { isActive: next });
    setErrors({});
  };
  const handleRowChange = (
    type: ShipType,
    region: ShipRegion,
    field: "from" | "to" | "price",
    value: string
  ) => {
    setRow(type, region, { [field]: value });
    if (field === "from") setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateShippingForm(form, t);
    if (Object.keys(errs).length) {
      setErrors(errs); // stay open (parity)
      return;
    }
    const model = buildShippingGraph(form, !!warehouse);
    dispatch(closeDialog()); // close FIRST (parity order)
    const result = await dispatch(changeShippingOptions(model));
    if (changeShippingOptions.fulfilled.match(result)) {
      dispatch(setSuccess(t("settings:shipping.saveSuccess"))); // FIX #1
    }
    await dispatch(getShippingOptions()); // refetch = read-back (parity)
  };

  const rowErrors = (
    type: ShipType,
    region: ShipRegion
  ): ShippingRegionRowFieldErrors => ({
    from: errors[`${type}.${region}.from`],
    to: errors[`${type}.${region}.to`],
    price: errors[`${type}.${region}.price`],
  });

  const tabs: { id: TabId; label: string }[] = [
    { id: "standart", label: t("settings:standart") },
    { id: "express", label: t("common:express") },
    { id: "pickUp", label: t("settings:pickUp") },
  ];

  const renderRows = (type: ShipType) => (
    <div style={{ display: tab === type ? "block" : "none" }}>
      {REGIONS.map((region) => (
        <ShippingRegionRowV2
          key={region}
          label={regionLabel(REGION_CODE[region], countryId, t)}
          row={form[type][region]}
          errors={rowErrors(type, region)}
          onToggle={(v) => handleRowToggle(type, region, v)}
          onChange={(field, v) => handleRowChange(type, region, field, v)}
          isPhone={isPhone}
        />
      ))}
    </div>
  );

  const footer = (
    <>
      <button type="button" className="btn btn-ghost" onClick={handleClose}>
        {tCommon("buttons.cancel")}
      </button>
      <button
        type="submit"
        form="shippingFormV2"
        className="btn btn-primary"
        style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)" }}
      >
        {tCommon("saveChanges")}
      </button>
    </>
  );

  return (
    <SettingsV2DialogShell
      open={open}
      onClose={handleClose}
      width={880}
      title={t("settings:editShippingOptions")}
      labelledById="sv2-shipping-dialog-title"
      footerNote={t("settings:shipping.dialogFooterNote")}
      footer={footer}
    >
      <form
        id="shippingFormV2"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        {/* Underline tabs (designer DlgShipping 162-169) — panels stay mounted. */}
        <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--border)" }}>
          {tabs.map((tb) => {
            const active = tb.id === tab;
            return (
              <button
                key={tb.id}
                type="button"
                onClick={() => setTab(tb.id)}
                style={{
                  position: "relative",
                  padding: "8px 12px 10px",
                  fontSize: 13,
                  cursor: "pointer",
                  background: "transparent",
                  border: 0,
                  fontFamily: "inherit",
                  color: active ? "var(--mod-accent)" : "var(--muted)",
                  fontWeight: active ? 600 : 400,
                }}
              >
                {tb.label}
                {active && (
                  <span
                    style={{
                      position: "absolute",
                      left: 8,
                      right: 8,
                      bottom: -1,
                      height: 2,
                      background: "var(--mod-accent)",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {renderRows("standart")}
        {renderRows("express")}

        {/* Pick-up tab */}
        <div style={{ display: tab === "pickUp" ? "block" : "none" }}>
          {warehouse ? (
            <div
              style={{
                background: form.isSelfPickUpActive ? "var(--bg-soft)" : "transparent",
                border: "1px solid var(--border-soft)",
                borderRadius: 10,
                padding: "14px 16px",
              }}
            >
              <SCheck
                on={form.isSelfPickUpActive}
                onToggle={(v) => setForm((f) => ({ ...f, isSelfPickUpActive: v }))}
                label={t("settings:fromWarehouse")}
              />
              <div style={{ marginTop: 12, paddingLeft: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)", marginBottom: 4 }}>
                  {tCommon("forms.address")}
                </div>
                <div style={{ fontSize: 13, color: "var(--ink)" }}>{warehouse.address}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                  {compressWeekDaysLocalized(warehouse.daysOfWeek || [], t)}, {formatHHmm(warehouse.opensAt)}–{formatHHmm(warehouse.closesAt)}
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: "14px 16px",
                border: "1px dashed var(--border)",
                borderRadius: 10,
                background: "var(--bg-soft)",
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
                {t("settings:noWarehouseForPickUp")}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 4, lineHeight: 1.55 }}>
                {t("settings:shipping.warehouseEditHint")}
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={goToCompany}
                style={{ marginTop: 10, color: "var(--mod-accent)" }}
              >
                {t("settings:goToCompanyInfo")}
              </button>
            </div>
          )}
        </div>
      </form>
    </SettingsV2DialogShell>
  );
};

export default ShippingDialogV2;
